import { useCallback, useEffect, useRef, useState } from "react";

const REPEAT_MS = 12_000;
const STORAGE_KEY = "ft.sound.enabled";

/**
 * Звуковое уведомление о новом оплаченном заказе (п. 14 ТЗ).
 *
 * Браузер не даёт играть звук без жеста пользователя, поэтому сотрудник один
 * раз включает звук, и дальше сигнал повторяется, пока заказ не принят.
 * Согласие запоминается, но AudioContext всё равно создаётся только по жесту.
 */
export function useAlarm(pendingCount: number) {
  const [enabled, setEnabled] = useState(false);
  const contextRef = useRef<AudioContext | null>(null);
  const pendingRef = useRef(pendingCount);

  pendingRef.current = pendingCount;

  const beep = useCallback(() => {
    const context = contextRef.current;
    if (!context) return;

    // Две короткие ноты: короткий сигнал не сливается с шумом кухни,
    // но и не превращается в сирену.
    [0, 0.22].forEach((offset, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime + offset;

      oscillator.type = "square";
      oscillator.frequency.value = index === 0 ? 660 : 880;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);

      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.2);
    });
  }, []);

  const enable = useCallback(() => {
    if (!contextRef.current) {
      contextRef.current = new AudioContext();
    }
    void contextRef.current.resume();
    localStorage.setItem(STORAGE_KEY, "1");
    setEnabled(true);
    beep();
  }, [beep]);

  const remembered = localStorage.getItem(STORAGE_KEY) === "1";

  useEffect(() => {
    if (!enabled || pendingCount === 0) return;

    beep();
    const timer = setInterval(() => {
      if (pendingRef.current > 0) beep();
    }, REPEAT_MS);

    return () => clearInterval(timer);
    // Намеренно зависим только от факта появления непринятых заказов:
    // иначе каждый новый заказ сбрасывал бы интервал повтора.
  }, [enabled, pendingCount > 0, beep]);

  return { enabled, remembered, enable };
}
